import config from '../config/index.json';
import userService from '../services/Login/index';
import { generateAxiosInstance } from '../utils/axios';
import store from '../redux/store';
import userActions from '../services/Login/Actions';
import { setOAuthToken, setOktaSessionID, setIdToken } from './tokenManager';
import {
  getValidatedStoreName,
  validateStoreName as validateStoreNameInternal,
  sanitizeStoreNameForPath as sanitizeStoreNameForPathInternal,
  validateRedirectURI as validateRedirectURIInternal,
} from './securityValidation';


const { VITE_S3_ENABLE } = import.meta.env;
const {
  VITE_API_URL, VITE_REDIRECT_TO_OLD_URL, VITE_OKTA_DOMAIN, VITE_OKTA_CLIENT_ID, VITE_NODE_ENV, VITE_OKTA_CLIENT_ID_EU, VITE_OKTA_GLOBAL_DOMAIN, VITE_GLOBAL_OKTA_OAUTH
} = import.meta.env;
const baseURL = {};
if (VITE_S3_ENABLE === 'true') {
  baseURL.baseURL = VITE_API_URL;
} else {
  baseURL.baseURL = '/';
}
const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
const code = urlParams.get('code');
const error = urlParams.get('error');
const state = urlParams.get('state');
const OAUTH_STATE_KEY = 'oauth_state';
const isUatOrProduction = VITE_NODE_ENV === 'UAT' || VITE_NODE_ENV === 'production';

// Strip OAuth params from URL immediately after extraction.
// Prevents auth code from persisting in browser history entries,
// server/proxy logs, and Referer headers on subsequent navigations.
// Before cleaning, signal to the rest of the app that this is an
// OAuth callback page load — consumers must use this flag instead
// of reading ?code= from the URL (which will no longer be present).
if (code) {
  sessionStorage.setItem('oauth_callback', 'true');
}
if (code || error || state) {
  const cleanParams = new URLSearchParams(queryString);
  cleanParams.delete('code');
  cleanParams.delete('error');
  cleanParams.delete('state');
  const cleanSearch = cleanParams.toString();
  const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${window.location.hash}`;
  window.history.replaceState(window.history.state, '', cleanUrl);
}
let utmQueryParams;
if (window.location.search.includes('utm') || window.location.hash?.includes('utm')) {
  const querySplit = window.location?.href.split('/');
  utmQueryParams = querySplit[querySplit.length - 1];
  store.dispatch({ type: 'SET_UTM_PARAMS', payload: utmQueryParams });
}
// ---------- PKCE Helpers ----------
export function generateCodeVerifier() {
  return generateRandomString(64);
}

function generateRandomString(length) {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const randomValues = crypto.getRandomValues(new Uint32Array(length));
  for (let i = 0; i < randomValues.length; i++) {
    result += charset[randomValues[i] % charset.length];
  }
  return result;
}

export async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function createOAuthState() {
  const oauthState = generateRandomString(64);
  sessionStorage.setItem(OAUTH_STATE_KEY, oauthState);
  sessionStorage.setItem('oauth_state_created_at', String(Date.now()));
  return oauthState;
}

export function generateState() {
  return createOAuthState();
}

export function validateState(receivedState) {
  const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  const createdAt = Number(sessionStorage.getItem('oauth_state_created_at'));
  const TTL_MS = 5 * 60 * 1000;

  if (!expectedState || !createdAt) {
    throw new Error('State not found or already used');
  }

  if (Date.now() - createdAt > TTL_MS) {
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem('oauth_state_created_at');
    throw new Error('State expired');
  }

  if (receivedState !== expectedState) {
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem('oauth_state_created_at');
    throw new Error('CSRF detected: State mismatch');
  }

  sessionStorage.removeItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem('oauth_state_created_at');
  return true;
}

export function cleanAuthorizationCodeFromUrl(search = window.location.search) {
  const params = new URLSearchParams(search);
  if (!params.has('code') && !params.has('state') && !params.has('error')) return;
  params.delete('code');
  params.delete('state');
  params.delete('error');
  const cleanSearch = params.toString();
  const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${window.location.hash}`;
  window.history.replaceState(window.history.state, '', cleanUrl);
}

export function validateStoreName(storeName) {
  return validateStoreNameInternal(storeName);
}

export function sanitizeStoreNameForPath(storeName) {
  return sanitizeStoreNameForPathInternal(storeName);
}

export function validateRedirectURI(origin = window.location.origin) {
  return validateRedirectURIInternal(origin);
}

function getValidatedRedirectOrigin(origin = window.location.origin) {
  validateRedirectURI(origin);
  return origin;
}

function validateOAuthState() {
  if (!code) return true;

  try {
    return validateState(state);
  } catch (_) {
    return false;
  }
}

export function handleOAuthCallback(search = window.location.search) {
  const params = new URLSearchParams(search);
  const authCode = params.get('code');
  const authState = params.get('state');
  const authError = params.get('error');

  if (authCode || authState || authError) {
    params.delete('code');
    params.delete('state');
    params.delete('error');
    const cleanSearch = params.toString();
    const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', cleanUrl);
  }

  return {
    code: authCode,
    state: authState,
    error: authError,
  };
}

// Not declared async so validation throws synchronously before any Promise is created.
// Callers that need to await the async work can await the returned Promise.
export function redirectToOkta(origin) {
  // Validate and resolve redirect origin — uses explicit origin when provided.
  const redirectOrigin = getValidatedRedirectOrigin(origin);

  // Async work is isolated inside an IIFE Promise so the sync validation above
  // can throw before any Promise exists — making it catchable with .toThrow().
  return (async () => {
    const codeVerifier = generateRandomString(64);
    // Persist in sessionStorage so the verifier survives the full-page redirect
    // to Okta and back. sessionStorage is tab-scoped and never transmitted to
    // the server; it is cleared immediately after the single token exchange.
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    const oauthState = createOAuthState();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    window.location.href = `${VITE_OKTA_GLOBAL_DOMAIN}oauth2/${VITE_GLOBAL_OKTA_OAUTH}/v1/authorize?` +
      `response_type=code&scope=openid profile email&client_id=${VITE_OKTA_CLIENT_ID_EU}` +
      `&state=${oauthState}&redirect_uri=${redirectOrigin}/store/` +
      `&code_challenge=${codeChallenge}&code_challenge_method=S256`;
  })();
}
function getCode() {
  const redirectOrigin = getValidatedRedirectOrigin();
  const redirectUrl = localStorage.getItem('redirectUrl');
  localStorage.setItem('savedCartItems', '');
  if (window.name !== '' && !isUatOrProduction) {
    const validatedWindowStoreName = getValidatedStoreName(window.name);
    localStorage.setItem('StoreName', validatedWindowStoreName);
    window.name = '';
  }

  let validatedStoreName;
  try {
    validatedStoreName = getValidatedStoreName(localStorage.getItem('StoreName'));
  } catch (err) {
    if (isUatOrProduction && (err?.message === 'Invalid store name' || err?.message === 'StoreName missing')) {
      window.location.href = VITE_REDIRECT_TO_OLD_URL;
      return;
    }
    throw err;
  }
  if (redirectUrl == null || window.location.pathname !== redirectUrl) {
    localStorage.setItem('redirectUrl', `${window.location.pathname.substring(window.location.pathname.lastIndexOf('/'))}${window.location.search}`);
  }
  if (utmQueryParams) {
    localStorage.setItem('redirectUrl', `/${utmQueryParams}`);
  }
  // Redirect to Okta with PKCE
    if(!code && (validatedStoreName === 'RESMED_EU' || window.name === 'RESMED_EU')) {
      redirectToOkta();
    } else if(!code) {
      const oauthState = createOAuthState();
      if (!error && validatedStoreName.includes('RESMED')) {
        window.location.href = `${VITE_OKTA_DOMAIN}oauth2/default/v1/authorize?response_type=code&scope=openid&client_id=${VITE_OKTA_CLIENT_ID}&state=${oauthState}&redirect_uri=${redirectOrigin}/store/`;
      } else if (error || !validatedStoreName.includes('RESMED')) {
        // eslint-disable-next-line no-alert
        window.location.href = isUatOrProduction ? VITE_REDIRECT_TO_OLD_URL : `${VITE_OKTA_DOMAIN}oauth2/default/v1/authorize?response_type=code&scope=openid&client_id=${VITE_OKTA_CLIENT_ID}&state=${oauthState}&redirect_uri=${redirectOrigin}/store/`;
      }
    }
}

function getRegisteredUserDetails() {
  const redirectOrigin = getValidatedRedirectOrigin();
  const registeredUserDetails = {
    'authorization-code': code,
    'original-redirect-uri': `${redirectOrigin}/store/`,
  };

  // If SPA flow (EU), include pkce verifier
  if (localStorage.getItem('StoreName')?.includes('RESMED_EU') || window.name === 'RESMED_EU') {
    registeredUserDetails.usepkce = true;
    // The verifier was written to sessionStorage just before the Okta redirect.
    // After the full-page round-trip the JS heap is fresh; sessionStorage is
    // the only reliable source. Remove it immediately — single-use.
    const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
    if (codeVerifier && registeredUserDetails['authorization-code']) {
      sessionStorage.removeItem('pkce_code_verifier');
    }
    registeredUserDetails['pkce-code-verifier'] = codeVerifier;
  } else {
    registeredUserDetails.usepkce = false;
  }

  return registeredUserDetails;
}

const getRegToken = async (axios) => {
  try {
    const registeredUserDetails = getRegisteredUserDetails();
    const safeStoreName = sanitizeStoreNameForPath(localStorage.getItem('StoreName'));
    const resp = await axios.post(`${config.api.path}/oauthac/${safeStoreName}/form?followlocation`, registeredUserDetails);
    store.dispatch(userActions.token({
      hasToken: Boolean(resp?.data?.['access-token']),
      'expires-in': resp?.data?.['expires-in'],
      'token-type': resp?.data?.['token-type'],
    }));
    if (resp?.data?.['access-token']) {
      const bearerToken = `Bearer ${resp?.data?.['access-token']}`;
      const storeName = getValidatedStoreName(localStorage.getItem('StoreName'));
      // Store token in memory instead of localStorage
      setOAuthToken(bearerToken, storeName);

      // Store id-token in memory for OIDC logout (NEVER in localStorage - security!)
      if (resp?.data?.['id-token']) {
        setIdToken(resp?.data?.['id-token']);
        let oktaUserId = '';
         try {
           const base64Url = resp?.data?.['id-token']?.split('.')?.[1];
           if (base64Url) {
             const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
             const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
             const jwtPayload = JSON.parse(atob(padded));
             oktaUserId = jwtPayload?.sub ?? '';
           }
         } catch (_) {
           oktaUserId = '';
         }
         store.dispatch({ type: 'OKTA_USERID', payload: oktaUserId });
      }
      
      // Remove from localStorage if it exists (cleanup)
      localStorage.removeItem(`${storeName}_oAuthToken`);
    } else   if (resp?.data?.includes('User not found') && 
         resp?.data?.includes('Customer must be synced to cloud before login')) {
      
      // Dispatch Redux action to show sync required error
      store.dispatch(userActions.failureSyncRequired(true));
      return;
    }
  } catch (err) {
    if (err?.message === 'Invalid store name' || err?.message === 'StoreName missing') {
      if (isUatOrProduction) {
        window.location.href = VITE_REDIRECT_TO_OLD_URL;
        return false;
      }
      throw err;
    }

    // Check for specific error condition: User not found and 400 status
    const isStatus400 = err?.response?.status === 400 || err?.status === 400;
    const errorData = err?.response?.data || err?.data || err?.message || '';
    const errorString = typeof errorData === 'string' ? errorData : JSON.stringify(errorData);
    
    if (isStatus400 && 
        (errorString.includes('User not found') && 
         errorString.includes('Customer must be synced to cloud before login'))) {
      
      // Dispatch Redux action to show sync required error
      store.dispatch(userActions.failureSyncRequired(true));
      return;
    }
    
    return false;
  }
};

function loginFromOkta() {
  if (!validateOAuthState()) {
    return Promise.reject(new Error('Invalid OAuth state parameter'));
  }

  return userService.getUserIdFromOkta().then((resp) => {
    // Store userLoginUsername in localStorage (as requested by user)
    localStorage.setItem('userLoginUsername', resp?.data?.login);
    // Store sensitive oktaSessionID in memory only
    setOktaSessionID(resp?.data?.id);
    // Remove oktaSessionID from localStorage if it exists (cleanup)
    localStorage.removeItem('oktaSessionID');
    userService.getTokenRemove();
    return userService.getToken().then((data) => {
      const axios = generateAxiosInstance({
        baseURL: baseURL.baseURL,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${data?.data?.token_type} ${data?.data?.access_token}`,
        },
      });
      return getRegToken(axios).then((result) => result !== false);
    });
  });
}

export default {
  loginFromOkta,
  getCode,
};
